import type { WSThemePagePublishResponse } from '../types';

import { useThemePagePublishMutation } from '~/api/graphql/mutations/themePagePublish.generated';
import { useWebsocketContext } from '../contexts';
import { generateId } from '~/utils/generateId';
import { useRef } from 'react';

type PendingRequests = {
  onSuccess: () => void;
  onError: () => void;
};

interface IUsePublishThemePageOption {
  themePageId: string;
  messageError?: string;
  forcePublish?: boolean;
  overwriteHandle?: boolean;
  onApiSuccess?: () => void;
}

interface UsePublishThemePageReturn {
  handlePublish: (
    onSuccess: () => void | Promise<void>,
    onError: () => void,
    option: IUsePublishThemePageOption,
  ) => void;
  listenWebSocketPublish: (callback: (data: WSThemePagePublishResponse) => void) => string;
  removeListenPublishSocket: () => void;
}

export const usePublishThemePage = (): UsePublishThemePageReturn => {
  const ws = useWebsocketContext();
  const themePagePublish = useThemePagePublishMutation();

  const unWatchPublish = useRef<Map<string, any>>(new Map());

  const pendingRequests = useRef<Map<string, PendingRequests>>(new Map<string, PendingRequests>());

  const globalListener = useRef<any>(null);

  const listenWebSocketPublish = (callback: (data: WSThemePagePublishResponse) => void) => {
    const listenerId = generateId();
    const listener = ws.onMessage<WSThemePagePublishResponse>('pagePublish', (data) => {
      callback(data as unknown as WSThemePagePublishResponse);
    });

    unWatchPublish.current.set(listenerId, listener);
    return listenerId;
  };

  const removeListenPublishSocket = () => {
    unWatchPublish.current.forEach((listener) => {
      ws.removeMessageListener('pagePublish', listener);
    });

    unWatchPublish.current.clear();
  };

  const setupGlobalListener = () => {
    if (globalListener.current) return;

    globalListener.current = ws.onMessage<WSThemePagePublishResponse>('pagePublish', (data) => {
      const themePageId = data.Data?.ThemePageID;
      if (!themePageId) return;

      const request = pendingRequests.current.get(themePageId);
      if (!request) return;

      if (data.Data?.Status === 'SUCCESS') {
        request.onSuccess();
        pendingRequests.current.delete(themePageId);
      } else if (data.Data?.Status === 'ERROR') {
        request.onError();
        pendingRequests.current.delete(themePageId);
      }

      if (pendingRequests.current.size === 0) {
        ws.removeMessageListener('pagePublish', globalListener.current);
        globalListener.current = null;
      }
    });
  };

  const handlePublish = async (
    onSuccess: () => void | Promise<void>,
    onError: () => void,
    option: IUsePublishThemePageOption,
  ) => {
    try {
      setupGlobalListener();

      pendingRequests.current.set(option.themePageId, { onSuccess, onError });

      await themePagePublish.mutateAsync({
        id: option.themePageId,
        ...(option.forcePublish ? { forcePublish: option.forcePublish } : {}),
        ...(option.overwriteHandle ? { overwriteHandle: option.overwriteHandle } : {}),
      });

      option.onApiSuccess?.();
    } catch (error) {
      pendingRequests.current.delete(option.themePageId);
      onError();
    }
  };

  return {
    handlePublish,
    listenWebSocketPublish,
    removeListenPublishSocket,
  };
};
